// src/components/CommonComponents/Pagination.js
import React from "react";

const Pagination = ({ currentPage, totalPages, onPageChange, totalCount, displayedSalesTotal }) => {
    const formattedDisplayedSales = Number.isFinite(displayedSalesTotal)
        ? new Intl.NumberFormat("en-PH", {
            style: "currency",
            currency: "PHP",
            minimumFractionDigits: 2,
        }).format(displayedSalesTotal)
        : null;

    const prevPage = () => currentPage > 1 && onPageChange(currentPage - 1);
    const nextPage = () => currentPage < totalPages && onPageChange(currentPage + 1);

    // Define window size (max 7 pages shown with ellipses)
    const maxPagesToShow = 7;
    const pages = [];
    if (totalPages <= maxPagesToShow) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
        const firstPage = 1;
        const lastPage = totalPages;
        const siblingCount = 1;
        const leftSibling = Math.max(currentPage - siblingCount, 2);
        const rightSibling = Math.min(currentPage + siblingCount, lastPage - 1);
        const showLeftDots = leftSibling > 2;
        const showRightDots = rightSibling < lastPage - 1;

        pages.push(firstPage);
        if (showLeftDots) {
            pages.push("dots-left");
        } else {
            for (let i = 2; i < leftSibling; i++) pages.push(i);
        }
        for (let i = leftSibling; i <= rightSibling; i++) pages.push(i);
        if (showRightDots) {
            pages.push("dots-right");
        } else {
            for (let i = rightSibling + 1; i < lastPage; i++) pages.push(i);
        }
        pages.push(lastPage);
    }

    return (
        <div className="w-full grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <div />
            <div className="flex items-center gap-1 justify-center">
            {/* Prev button */}
            <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded-md text-xs font-medium focus:outline-none ${currentPage === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "text-white"
                    }`}
                style={currentPage === 1 ? {} : {
                    background: 'var(--color-primary)',
                }}
                onMouseOver={currentPage === 1 ? undefined : (e => e.currentTarget.style.background = 'var(--color-hover)')}
                onMouseOut={currentPage === 1 ? undefined : (e => e.currentTarget.style.background = 'var(--color-primary)')}
            >
                Prev
            </button>

            {/* Page numbers (max 7) */}
            {pages.map((page, idx) => {
                if (typeof page !== "number") {
                    return (
                        <span key={`${page}-${idx}`} className="px-2 py-1 text-xs text-gray-400">
                            ...
                        </span>
                    );
                }
                return (
                    <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        className={`px-2 py-1 rounded-md text-xs font-semibold border focus:outline-none ${currentPage === page
                            ? "text-white"
                            : "bg-white hover:bg-gray-100"
                            }`}
                        style={currentPage === page
                            ? { background: 'var(--color-primary)', borderColor: 'var(--color-primary)' }
                            : { color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }
                        }
                    >
                        {page}
                    </button>
                );
            })}

            {/* Next button */}
            <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`px-2 py-1 rounded-md text-xs font-medium focus:outline-none ${currentPage === totalPages
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "text-white"
                    }`}
                style={currentPage === totalPages ? {} : {
                    background: 'var(--color-primary)',
                }}
                onMouseOver={currentPage === totalPages ? undefined : (e => e.currentTarget.style.background = 'var(--color-hover)')}
                onMouseOut={currentPage === totalPages ? undefined : (e => e.currentTarget.style.background = 'var(--color-primary)')}
            >
                Next
            </button>
            </div>
            <div className="flex justify-end">
                {typeof totalCount === "number" && (
                    <span className="text-xs text-gray-1000 font-normal">
                        Total: <span className="font-bold">{totalCount}</span>
                        {formattedDisplayedSales ? (
                            <>
                                {" | Sales: "}
                                <span className="font-bold">{formattedDisplayedSales}</span>
                            </>
                        ) : ""}
                    </span>
                )}
            </div>
        </div>
    );
};

export default Pagination;
